import React, { useCallback, useState, useEffect } from "react";
import { PlusCircle, Save, Eye, Edit, CheckCircle } from "lucide-react";
import { useCriteriaSettings } from "../../../hooks/useCriteriaSettings";
import { CriterionCard } from "../../../components/judging/CriterionCard";
import { ModeToggle } from "../../../components/ui/ModeToggle";
import { CardHeader } from "../../../components/ui/CardHeader";
import { LoadingSpinner } from "../../../components/ui/LoadingSpinner";
import { ErrorAlert } from "../../../components/ui/ErrorAlert";
import { useTheme } from "../../../context/ThemeContext";
import CriteriaPreview from "../../../components/CriteriaPreview";

/**
 * Settings component for managing judging criteria
 */
export function CriteriaSettings() {
  const { theme } = useTheme();
  const isDarkMode = theme === 'dark';
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [previewOrder, setPreviewOrder] = useState([]);
  const [lastValidDropIndex, setLastValidDropIndex] = useState(null);
  
  const {
    criteriaList,
    isPercentageMode,
    scoringMode,
    loading,
    error,
    hasChanges,
    totalPoints,
    successMessage,
    addCriterion,
    updateCriterion,
    removeCriterion,
    reorderCriteria,
    saveCriteria,
    togglePercentageMode,
    toggleScoringMode
  } = useCriteriaSettings();

  // Initialize preview order when criteria list changes
  useEffect(() => {
    setPreviewOrder(criteriaList.map((_, index) => index));
  }, [criteriaList]);

  const handleDragStart = useCallback((index) => {
    setDraggedIndex(index);
  }, []);

  const handleDragEnd = useCallback(() => {
    // If we have a valid drop position from hovering, use it
    if (draggedIndex !== null && lastValidDropIndex !== null) {
      if (typeof reorderCriteria === 'function') {
        reorderCriteria(draggedIndex, lastValidDropIndex);
      }
    }
    
    // Reset all drag state
    setDraggedIndex(null);
    setPreviewOrder(criteriaList.map((_, index) => index));
    setLastValidDropIndex(null);
  }, [criteriaList, draggedIndex, lastValidDropIndex, reorderCriteria]);

  const handleDragHover = useCallback((dragIndex, targetIndex) => {
    if (dragIndex === targetIndex) return;
    
    // Store the last valid drop index
    setLastValidDropIndex(targetIndex);
    
    // Create new preview order
    const newOrder = [...previewOrder];
    const draggedItem = newOrder[dragIndex];
    
    // Remove dragged item
    newOrder.splice(dragIndex, 1);
    
    // Insert at target position
    const insertIndex = targetIndex > dragIndex ? targetIndex - 1 : targetIndex;
    newOrder.splice(insertIndex, 0, draggedItem);
    
    setPreviewOrder(newOrder);
  }, [previewOrder]);

  const handleReorder = useCallback((dragIndex, dropIndex) => {
    // Reset preview state
    setDraggedIndex(null);
    setPreviewOrder(criteriaList.map((_, index) => index));
    setLastValidDropIndex(null);
    
    // Use the reorderCriteria function from the hook if it exists
    if (typeof reorderCriteria === 'function') {
      reorderCriteria(dragIndex, dropIndex);
    }
  }, [reorderCriteria, criteriaList]);

  // Container-level drag handlers for more forgiving drop zones
  const handleContainerDragOver = useCallback((e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
  }, []);

  const handleContainerDrop = useCallback((e) => {
    e.preventDefault();
    
    // If we have a valid drop position from hovering, use it
    if (draggedIndex !== null && lastValidDropIndex !== null) {
      if (typeof reorderCriteria === 'function') {
        reorderCriteria(draggedIndex, lastValidDropIndex);
      }
    }
    
    // Reset all drag state
    setDraggedIndex(null);
    setPreviewOrder(criteriaList.map((_, index) => index));
    setLastValidDropIndex(null);
  }, [draggedIndex, lastValidDropIndex, reorderCriteria, criteriaList]);

  const handleSave = useCallback(async () => {
    await saveCriteria();
  }, [saveCriteria]);

  if (loading && criteriaList.length === 0) {
    return <LoadingSpinner />;
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* Criteria Settings */}
      <div className="card bg-base-100 border border-base-300">
        <div className="card-body p-6">
          <CardHeader
            icon={<Edit />}
            title="Judging Criteria"
            description={
              scoringMode === 'simple' 
                ? "Simple scoring: Judges give one total score"
                : isPercentageMode
                ? "Percentages must total 100%"
                : "Each criterion can have up to 100 points"
            }
          />

          {/* Scoring Mode Toggle */}
          <div className="mb-6 p-4 bg-base-50 rounded-lg border border-base-300">
            <h3 className="text-lg font-semibold mb-3">Scoring Style</h3>
            <div className="flex flex-col sm:flex-row gap-4">
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="radio"
                  name="scoringMode"
                  value="criteria"
                  checked={scoringMode === 'criteria'}
                  onChange={() => toggleScoringMode('criteria')}
                  className="radio radio-primary"
                />
                <div>
                  <div className="font-medium">Criteria-Based Scoring</div>
                  <div className="text-sm text-base-content/60">
                    Judges score multiple categories (technique, performance, etc.)
                  </div>
                </div>
              </label>
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="radio"
                  name="scoringMode"
                  value="simple"
                  checked={scoringMode === 'simple'}
                  onChange={() => toggleScoringMode('simple')}
                  className="radio radio-primary"
                />
                <div>
                  <div className="font-medium">Simple Total Score</div>
                  <div className="text-sm text-base-content/60">
                    Judges give one overall score for the entire performance
                  </div>
                </div>
              </label>
            </div>
          </div>

          {scoringMode === 'criteria' && (
          <div className="flex justify-between items-center mb-4">
            <ModeToggle
              isPercentageMode={isPercentageMode}
              onChange={togglePercentageMode}
            />
            <div
              className={`badge ${
                totalPoints > 100
                  ? "badge-error"
                  : isPercentageMode && totalPoints === 100
                  ? "badge-success"
                  : "badge-ghost"
              }`}
            >
              Total: {totalPoints}
              {isPercentageMode ? "%" : " points"}
            </div>
          </div>
          )}

          <ErrorAlert 
            message={error}
            className="mt-4"
          />

          {successMessage && (
            <div className="alert alert-success mt-4">
              <CheckCircle className="w-5 h-5" />
              <span>{successMessage}</span>
            </div>
          )}

          {scoringMode === 'simple' ? (
            <div className="text-center py-8">
              <div className="bg-primary/10 rounded-lg p-6">
                <h3 className="text-lg font-semibold mb-2">Simple Scoring Mode</h3>
                <p className="text-base-content/70 mb-4">
                  In simple scoring mode, judges will give one overall score for each performance.
                  No individual criteria need to be configured.
                </p>
                <div className="text-sm text-base-content/60">
                  Judges will see a single slider or input field to score the entire performance.
                </div>
              </div>
            </div>
          ) : (
            <>
          {isPercentageMode && totalPoints !== 100 && totalPoints > 0 && (
            <div className="alert alert-warning mt-4">
              <p>Total percentage must equal 100% before saving.</p>
            </div>
          )}

          <div 
            className="space-y-4 mt-4 min-h-[100px]"
            onDragOver={handleContainerDragOver}
            onDrop={handleContainerDrop}
          >
            {(draggedIndex !== null ? previewOrder : criteriaList.map((_, i) => i)).map((originalIndex, displayIndex) => {
              const criterion = criteriaList[originalIndex];
              if (!criterion) return null;
              
              return (
                <CriterionCard
                  key={criterion.id}
                  criterion={criterion}
                  index={originalIndex}
                  onChange={updateCriterion}
                  onRemove={removeCriterion}
                  onReorder={handleReorder}
                  isPercentageMode={isPercentageMode}
                  draggedIndex={draggedIndex}
                  onDragHover={handleDragHover}
                  onDragStart={handleDragStart}
                  onDragEnd={handleDragEnd}
                />
              );
            })}

            <button
              onClick={addCriterion}
              className="btn btn-outline btn-block btn-sm mt-4"
            >
              <PlusCircle className="w-4 h-4 mr-2" />
              Add Criterion
            </button>
          </div>
            </>
          )}

          <div className="card-actions justify-end mt-6">
            <button
              onClick={handleSave}
              disabled={
                !hasChanges ||
                (scoringMode === 'criteria' && (
                totalPoints > 100 ||
                (isPercentageMode && totalPoints !== 100)
                ))
              }
              className="btn btn-primary"
            >
              <Save className="w-4 h-4 mr-2" />
              Save {scoringMode === 'simple' ? 'Scoring Mode' : 'Criteria'}
            </button>
          </div>
        </div>
      </div>

      {/* Live Preview */}
      <div className="card bg-base-100 border border-base-300">
        <div className="card-body p-6">
          <CardHeader
            icon={<Eye />}
            title={scoringMode === 'simple' ? 'Simple Scoring Preview' : 'Criteria Preview'}
            description={scoringMode === 'simple' 
              ? 'See how simple scoring will appear to judges'
              : 'See how your criteria will appear to judges'
            }
          />
          
          {scoringMode === 'simple' ? (
            <div className="bg-base-50 rounded-lg p-6 border border-base-300">
              <h3 className="text-lg font-semibold mb-4">Judge Scoring Interface</h3>
              
              {/* Simple scoring card preview with TouchScoringCard styling */}
              <div
                className={`relative overflow-hidden rounded-xl border-2 transition-all duration-100 ${
                  isDarkMode
                    ? "border-gray-600 bg-gray-800/50"
                    : "border-gray-300 bg-white"
                } opacity-90`}
                style={{ 
                  minHeight: '70px',
                  userSelect: 'none',
                  WebkitUserSelect: 'none'
                }}
              >
                {/* Progress fill background */}
                <div 
                  className="absolute inset-0"
                  style={{
                    transform: `scaleX(${85 / 100})`,
                    transformOrigin: 'left',
                    background: `linear-gradient(to right, rgb(236 72 153 / 0.3) 0%, rgb(147 51 234 / 0.3) 100%)`,
                    transition: 'transform 150ms cubic-bezier(0.4, 0, 0.2, 1)'
                  }}
                />
                
                {/* Content */}
                <div className="relative z-10 p-3">
                  <div className="flex justify-between items-center h-full">
                    <div className="flex-1 pr-2 sm:pr-4 min-w-0">
                      <h4 className={`font-bold text-sm uppercase tracking-wide truncate ${
                        isDarkMode ? "text-white" : "text-gray-800"
                      }`}>
                        OVERALL PERFORMANCE
                      </h4>
                      <p className={`text-xs mt-0.5 leading-tight ${
                        isDarkMode ? "text-gray-300" : "text-gray-600"
                      }`}>
                        Tap and drag to score
                      </p>
                    </div>
                    
                    <div className="text-right flex-shrink-0">
                      <div className="text-2xl font-bold transition-transform duration-200 text-blue-500">
                        85.0
                      </div>
                      <div className={`text-sm ${
                        isDarkMode ? "text-gray-400" : "text-gray-500"
                      }`}>
                        of 100
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Score markers for reference */}
                <div className="absolute bottom-1 left-2 right-2 flex justify-between pointer-events-none opacity-60">
                  {Array.from({ length: 6 }, (_, i) => {
                    const value = i * 20;
                    return (
                      <div 
                        key={value}
                        className={`text-xs ${
                          isDarkMode ? "text-gray-600" : "text-gray-400"
                        }`}
                      >
                        {value}
                      </div>
                    );
                  })}
                </div>
                
                {/* Preview indicator */}
                <div className="absolute top-1 right-1 opacity-50">
                  <div className={`text-xs ${
                    isDarkMode ? "text-gray-400" : "text-gray-500"
                  } bg-black/20 rounded px-1`}>
                    PREVIEW
                  </div>
                </div>
              </div>
              
              <div className="text-sm text-base-content/60 mt-4">
                Judges will use a single interactive card to score the entire performance from 0-100 points.
              </div>
            </div>
          ) : (
          <CriteriaPreview
            criteria={criteriaList}
            isDarkMode={isDarkMode}
            showTitle={false} // Title is already shown by CardHeader
          />
          )}
        </div>
      </div>
    </div>
  );
} 